import { IonRouterLink } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import InitialConsultMedicalForm from '@components/forms/InitialConsultMedicalForm';
import PersonalizeForm from '@components/forms/PersonalizeForm';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import Modal from '@components/Modal';
import PMEQuestion from '@components/PMEQuestion';
import styles from '@components/profile/Profile.module.css';
import Icon from '@components/text/Icon';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import {
  CareProfileActionableFieldEnum,
  CareProfileMedicalConditionEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { InitialConsultFields, PersonalizeExperienceFields } from '@typing/Models';
type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const Medical = ({ journey }: Props) => {
  const opener = useOpener();
  const intl = useIntl();
  const personalizeMedicalOpener = useOpener();
  const initialConsultOpener = useOpener();

  const [editingQuestion, setEditingQuestion] = useState<PersonalizeExperienceFields[]>([]);
  const [editingInitialConsult, setEditingInitialConsult] = useState<InitialConsultFields[] | undefined>(undefined);

  const handlePMEQuestionChange = useCallback(
    (field: PersonalizeExperienceFields) => () => {
      setEditingQuestion([field]);
      personalizeMedicalOpener.open();
    },
    [personalizeMedicalOpener]
  );

  const handleInitialConsultQuestionChange = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultOpener.open();
    },
    [initialConsultOpener]
  );

  const medicalConditions = useMemo(() => {
    const conditions =
      journey.careProfile.medicalConditions?.medicalConditionsOptions.map(option =>
        intl.formatMessage({
          id: `models.pme.questions.medical_conditions.options.${option}.content`
        })
      ) ?? [];

    if (journey.careProfile.medicalConditions?.other) {
      conditions?.push(journey.careProfile.medicalConditions.other);
    }
    return conditions;
  }, [
    journey.careProfile.medicalConditions?.medicalConditionsOptions,
    journey.careProfile.medicalConditions?.other,
    intl
  ]);

  return (
    <div>
      <IonRouterLink onClick={opener.toggle}>
        <div className={styles.section}>
          <Heading2>
            <FormattedMessage id="pages.client.careProfile.sections.medical" />
          </Heading2>
          <Icon
            ariaLabelKey="accessibility.ariaLabels.toggle"
            className={styles.toggleOpener}
            icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
          />
        </div>
      </IonRouterLink>
      <ExpandableSection isOpen={opener.isOpen}>
        <PMEQuestion
          field={CareProfileActionableFieldEnum.medical_conditions}
          handleEdit={handlePMEQuestionChange}
          selections={medicalConditions}
        />
        {journey.careProfile.medicalConditions?.medicalConditionsOptions.includes(
          CareProfileMedicalConditionEnum.dementia
        ) && (
          <InitialConsultQuestion
            field={InitialConsultFields.DEMENTIA_SEVERITY}
            handleEdit={handleInitialConsultQuestionChange}
            selections={[
              journey.careProfile.dementiaSeverity
                ? intl.formatMessage({
                    id: `models.initialConsultReport.questions.dementia_severity.options.${journey.careProfile.dementiaSeverity}.content`
                  })
                : null
            ]}
            titleKey="models.initialConsultReport.questions.dementia_severity.content"
          />
        )}
        <InitialConsultQuestion
          field={InitialConsultFields.RECENT_HOSPITALIZATION}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.recentHospitalization
              ? intl.formatMessage({ id: 'dictionary.yes' })
              : journey.careProfile.recentHospitalization === false
              ? intl.formatMessage({ id: 'dictionary.no' })
              : null
          ]}
          titleKey="models.initialConsultReport.questions.recent_hospitalization.content"
        />
        {journey.careProfile.recentHospitalization && (
          <InitialConsultQuestion
            selections={[journey.careProfile.recentHospitalizationSynopsis]}
            titleKey="models.initialConsultReport.questions.recent_hospitalization.synopsis"
          />
        )}
        <InitialConsultQuestion
          field={InitialConsultFields.MEDICATION_ASSISTANCE_NEEDS}
          handleEdit={handleInitialConsultQuestionChange}
          selections={
            journey.careProfile.medicationAssistanceNeeds?.map(option =>
              intl.formatMessage({
                id: `models.careProfile.questions.medication_assistance_needs.options.${option}.content`
              })
            ) ?? []
          }
          titleKey="models.careProfile.questions.medication_assistance_needs.content"
        />
        <InitialConsultQuestion
          field={InitialConsultFields.MEDICATIONS_AFFORDABLE}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.medicationsAffordable
              ? intl.formatMessage({
                  id: `models.careProfile.questions.medications_affordable.options.${journey.careProfile.medicationsAffordable}.content`
                })
              : null
          ]}
          titleKey="models.careProfile.questions.medications_affordable.content"
        />
        <InitialConsultQuestion
          field={InitialConsultFields.PRIMARY_CARE_PROVIDER_STATUS}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.primaryCareProviderStatus
              ? intl.formatMessage({
                  id: `models.careProfile.questions.primary_care_provider_status.options.${journey.careProfile.primaryCareProviderStatus}.content`
                })
              : null
          ]}
          titleKey="models.careProfile.questions.primary_care_provider_status.content"
        />
      </ExpandableSection>

      <Modal opener={initialConsultOpener} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
        {() => (
          <InitialConsultMedicalForm
            careProfile={journey.careProfile}
            consultId={journey.initialConsultReport.consult?.id ?? null}
            initialConsultReportId={journey.initialConsultReport.id}
            onCancel={initialConsultOpener.close}
            onSuccess={initialConsultOpener.close}
            questions={editingInitialConsult}
          />
        )}
      </Modal>
      <Modal opener={personalizeMedicalOpener} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
        {() => (
          <PersonalizeForm
            careProfile={journey.careProfile}
            clientRole={null}
            onCancel={personalizeMedicalOpener.close}
            onSuccess={personalizeMedicalOpener.close}
            questions={editingQuestion}
          />
        )}
      </Modal>
    </div>
  );
};

export default Medical;
